import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import classNames from 'classnames';
import { rgba } from 'polished';

import Sizes from '../definitions/Sizes';
import Colours from '../definitions/Colours';
import BreadcrumbStepPropType from '../definitions/BreadcrumbStepPropType';
import RemoveDefaultButtonStyles from '../styles/RemoveDefaultButtonStyles';
import getBreadcrumbStatus, { isStepActive } from '../functions/getBreadcrumbStatus';
import IconChevronUp from './icons/IconChevronUp';
import IconChevronDown from './icons/IconChevronDown';
import AccordionBase from './AccordionBase';
import Breadcrumb from './Breadcrumb';


function BreadcrumbAccordion({ step, stepNumber, onBreadcrumbClick, ...otherProps }) {
    const [ isExpanded, setIsExpanded ] = useState(false);

    const {
        isActive,
        isCompleted,
        isDisabled,
    } = getBreadcrumbStatus(step);

    /* Requirement: If we've become active, force accordion to open.
        When we've become inactive, force the accordion to close.  */
    useEffect(() => {
        if (isActive) {
            setIsExpanded(true);
        } else {
            setIsExpanded(false);
        }
    }, [ isActive ]);


    function handleAccordionClick() {
        /* Requirements: Disabled accordions cannot be opened, active accordions cannot be closed */
        if (isDisabled || isActive) {
            return;
        }

        setIsExpanded(prevIsExpanded => !prevIsExpanded);
    }


    return (
        <AccordionBase
            name={step.id}
            isExpanded={isExpanded}
            alwaysRenderChildMarkup
            onClick={handleAccordionClick}

            HeaderComponent={AccordionHeader}
            headerProps={{
                step,
                stepNumber,

                isActive,
                isCompleted,
                isDisabled,
            }}

            {...otherProps}
        >
            <ul className="breadcrumb-accordion-list">
                <For
                    each="subStep"
                    of={step.steps}
                >
                    <li
                        key={subStep.id}
                        className="breadcrumb-accordion-list-item"
                        aria-current={!subStep.steps && isStepActive(subStep)}
                    >
                        <Choose>
                            <When condition={subStep.steps?.length > 0}>
                                <StyledBreadcrumbAccordion
                                    step={subStep}
                                    onBreadcrumbClick={onBreadcrumbClick}
                                    className="reduce-list-padding"
                                />
                            </When>
                            <Otherwise>
                                <Breadcrumb
                                    key={subStep.id}
                                    {...subStep}

                                    onClick={onBreadcrumbClick ? () => onBreadcrumbClick(subStep) : undefined}
                                    data-name={onBreadcrumbClick ? subStep.id : undefined}

                                    descriptor={step.stepDescriptor}
                                    values={step.stepValues}
                                />
                            </Otherwise>
                        </Choose>
                    </li>
                </For>
            </ul>
        </AccordionBase>
    );
}

BreadcrumbAccordion.propTypes = {
    step: BreadcrumbStepPropType.isRequired,
    stepNumber: PropTypes.number,
    onBreadcrumbClick: PropTypes.func,
};

// eslint-disable-next-line react/no-multi-comp, react/prop-types
function AccordionHeader({ step, stepNumber, isExpanded, isActive, isCompleted, isDisabled, onClick, ...otherProps }) {
    const IconChevron = isExpanded ? IconChevronUp : IconChevronDown;

    return (
        <button
            type="button"
            className={classNames('accordion-header-button', {
                'is-active': isActive,
            })}
            data-name="toggle-breadcrumb-accordion"
            onClick={onClick}

            {...otherProps}

            /* Cannot interact when active or disabled */
            disabled={isDisabled || isActive}
        >
            <Breadcrumb
                className="header-breadcrumb"
                {...step}
                // eslint-disable-next-line react/prop-types
                descriptor={step.stepDescriptor}
                // eslint-disable-next-line react/prop-types
                values={step.stepValues}

                isActive={isActive}
                isCompleted={isCompleted}
                isDisabled={isDisabled}

                stepNumber={stepNumber}
            />
            <IconChevron
                className="icon-chevron"

                width={Sizes.ICONS.S}
                height={Sizes.ICONS.S}

                strokeWidth="3"
                outlineWidth="0"
            />
        </button>
    );
}

const StyledBreadcrumbAccordion = styled(observer(BreadcrumbAccordion))`
    width: 100%;

    .breadcrumb-accordion-list {
        list-style: none;
        padding-left: ${Sizes.SPACING.TWO};

        > .breadcrumb-accordion-list-item {
            margin: ${Sizes.SPACING.HALF} 0;
        }
    }

    &.reduce-list-padding .breadcrumb-accordion-list {
        padding-left: ${Sizes.SPACING.HALF};
    }

    .accordion-header-button {
        ${RemoveDefaultButtonStyles};

        display: flex;
        flex-wrap: nowrap;
        justify-content: space-between;

        width: 100%;

        border-radius: 2px;

        > .icon-chevron {
            flex-shrink: 0;
            margin-top: ${Sizes.SPACING.QUARTER};

            stroke: ${Colours.BLACKBERRY};
        }

        &:hover {
            > .header-breadcrumb {
                color: ${Colours.BLUEBERRY};
            }

            > .icon-chevron {
                stroke: ${Colours.BLUEBERRY};
            }
        }

        &:focus {
            box-shadow: 0px 0px 2px 2px ${rgba(Colours.BLUEBERRY, 0.4)};
        }

        &:active {
            > .header-breadcrumb {
                color: ${Colours.BLUEBERRY_DARK};
            }

            > .icon-chevron {
                stroke: ${Colours.BLUEBERRY_DARK};
            }
        }

        &:disabled {
            > .icon-chevron {
                stroke: ${Colours.STONE_DARK};
            }
        }

        &.is-active:disabled {
            > .header-breadcrumb {
                color: ${Colours.BLACKBERRY};
            }

            > .icon-chevron {
                stroke: ${Colours.BLACKBERRY};
            }
        }
    }
`;

export default StyledBreadcrumbAccordion;
